/**
 * §28: титры.
 *
 * Хранятся пословно, а не строкой: активное слово, перенос строк и ручная
 * правка тайминга требуют знать границы каждого слова. Собрать слова в строку
 * можно всегда, разобрать строку обратно на слова с таймингами — нет.
 */
import { z } from 'zod';

export const captionWordSchema = z
  .object({
    text: z.string().trim().min(1).max(60),
    /** Секунды от начала сцены. */
    start: z.number().min(0).max(600),
    end: z.number().min(0).max(600),
  })
  .strict()
  // Слово не может закончиться раньше, чем началось. Проверка не теоретическая:
  // такие титры принимались через ручную правку, а дальше по ним считается
  // длительность показа строки — и она выходила отрицательной.
  .refine((word) => word.end >= word.start, {
    message: 'Слово не может заканчиваться раньше, чем начинается',
    path: ['end'],
  });

/** Строка титров: то, что одновременно видно на экране. */
export const captionLineSchema = z
  .object({
    words: z.array(captionWordSchema).min(1).max(16),
  })
  .strict();

export const sceneCaptionsSchema = z
  .object({
    enabled: z.boolean().default(true),
    /** Переопределение положения из бренд-кита для одной сцены (§28). */
    position: z.enum(['top', 'center', 'bottom']).nullable().default(null),
    lines: z.array(captionLineSchema).max(60).default([]),
    /**
     * Откуда взяты тайминги: `provider` — от синтеза речи, `estimated` —
     * расчёт по длительности. Пользователь должен понимать, насколько им верить.
     */
    source: z.enum(['provider', 'estimated', 'manual']).default('estimated'),
  })
  .strict();

export type CaptionWord = z.infer<typeof captionWordSchema>;
export type CaptionLine = z.infer<typeof captionLineSchema>;
export type SceneCaptions = z.infer<typeof sceneCaptionsSchema>;

/** Правка титров с экрана §67: приходит целиком, чтобы не рассинхронить строки. */
export const updateCaptionsSchema = z
  .object({
    enabled: z.boolean().optional(),
    position: z.enum(['top', 'center', 'bottom']).nullable().optional(),
    lines: z.array(captionLineSchema).max(60).optional(),
  })
  .strict();

export type UpdateCaptionsInput = z.infer<typeof updateCaptionsSchema>;

/** Плоский текст строки — для показа и сравнения. */
export const lineText = (line: CaptionLine): string =>
  line.words.map((word) => word.text).join(' ');
